import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building,
  Plus,
  Search,
  Download,
  Edit,
  Trash2,
  Eye,
  Phone,
  Mail,
  MapPin,
  User,
  Factory,
  Warehouse,
  Home,
  Server,
  FlaskConical,
  Store,
  Wrench,
  X,
  Check,
  MapPinned,
  Globe,
  FileSpreadsheet,
  FileText,
  ChevronDown
} from 'lucide-react';
import { branchService } from '../../../services/branchService';
import { exportToCSV, exportToExcel, getDateString } from '../../../utils/exportUtils';
import UsageMeter from '../../../components/UsageMeter';
import OverageConsentModal from '../../../components/OverageConsentModal';
import { subscriptionService } from '../../../services/subscriptionService';

const COUNTRY_TIMEZONES = {
  'Afghanistan': ['Asia/Kabul'],
  'Albania': ['Europe/Tirane'],
  'Algeria': ['Africa/Algiers'],
  'Angola': ['Africa/Luanda'],
  'Argentina': ['America/Argentina/Buenos_Aires', 'America/Argentina/Cordoba', 'America/Argentina/Mendoza'],
  'Armenia': ['Asia/Yerevan'],
  'Australia': ['Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Perth', 'Australia/Adelaide', 'Australia/Darwin', 'Australia/Hobart'],
  'Austria': ['Europe/Vienna'],
  'Azerbaijan': ['Asia/Baku'],
  'Bahrain': ['Asia/Bahrain'],
  'Bangladesh': ['Asia/Dhaka'],
  'Belarus': ['Europe/Minsk'],
  'Belgium': ['Europe/Brussels'],
  'Bolivia': ['America/La_Paz'],
  'Bosnia and Herzegovina': ['Europe/Sarajevo'],
  'Brazil': ['America/Sao_Paulo', 'America/Manaus', 'America/Belem', 'America/Fortaleza', 'America/Recife', 'America/Cuiaba', 'America/Porto_Velho', 'America/Boa_Vista', 'America/Noronha'],
  'Bulgaria': ['Europe/Sofia'],
  'Cambodia': ['Asia/Phnom_Penh'],
  'Cameroon': ['Africa/Douala'],
  'Canada': ['America/Toronto', 'America/Vancouver', 'America/Edmonton', 'America/Winnipeg', 'America/Halifax', 'America/St_Johns', 'America/Regina'],
  'Chile': ['America/Santiago', 'Pacific/Easter'],
  'China': ['Asia/Shanghai'],
  'Colombia': ['America/Bogota'],
  'Congo': ['Africa/Brazzaville'],
  'Costa Rica': ['America/Costa_Rica'],
  'Croatia': ['Europe/Zagreb'],
  'Cuba': ['America/Havana'],
  'Cyprus': ['Asia/Nicosia'],
  'Czech Republic': ['Europe/Prague'],
  'Denmark': ['Europe/Copenhagen'],
  'Dominican Republic': ['America/Santo_Domingo'],
  'Ecuador': ['America/Guayaquil', 'Pacific/Galapagos'],
  'Egypt': ['Africa/Cairo'],
  'El Salvador': ['America/El_Salvador'],
  'Estonia': ['Europe/Tallinn'],
  'Ethiopia': ['Africa/Addis_Ababa'],
  'Finland': ['Europe/Helsinki'],
  'France': ['Europe/Paris'],
  'Georgia': ['Asia/Tbilisi'],
  'Germany': ['Europe/Berlin'],
  'Ghana': ['Africa/Accra'],
  'Greece': ['Europe/Athens'],
  'Guatemala': ['America/Guatemala'],
  'Honduras': ['America/Tegucigalpa'],
  'Hong Kong': ['Asia/Hong_Kong'],
  'Hungary': ['Europe/Budapest'],
  'Iceland': ['Atlantic/Reykjavik'],
  'India': ['Asia/Kolkata'],
  'Indonesia': ['Asia/Jakarta', 'Asia/Makassar', 'Asia/Jayapura'],
  'Iran': ['Asia/Tehran'],
  'Iraq': ['Asia/Baghdad'],
  'Ireland': ['Europe/Dublin'],
  'Italy': ['Europe/Rome'],
  'Jamaica': ['America/Jamaica'],
  'Japan': ['Asia/Tokyo'],
  'Jordan': ['Asia/Amman'],
  'Kazakhstan': ['Asia/Almaty', 'Asia/Aqtau', 'Asia/Aqtobe'],
  'Kenya': ['Africa/Nairobi'],
  'Kuwait': ['Asia/Kuwait'],
  'Kyrgyzstan': ['Asia/Bishkek'],
  'Laos': ['Asia/Vientiane'],
  'Latvia': ['Europe/Riga'],
  'Lebanon': ['Asia/Beirut'],
  'Libya': ['Africa/Tripoli'],
  'Lithuania': ['Europe/Vilnius'],
  'Luxembourg': ['Europe/Luxembourg'],
  'Malaysia': ['Asia/Kuala_Lumpur'],
  'Malta': ['Europe/Malta'],
  'Mexico': ['America/Mexico_City', 'America/Cancun', 'America/Merida', 'America/Monterrey', 'America/Mazatlan', 'America/Chihuahua', 'America/Hermosillo', 'America/Tijuana'],
  'Moldova': ['Europe/Chisinau'],
  'Morocco': ['Africa/Casablanca'],
  'Myanmar': ['Asia/Rangoon'],
  'Nepal': ['Asia/Kathmandu'],
  'Netherlands': ['Europe/Amsterdam'],
  'New Zealand': ['Pacific/Auckland', 'Pacific/Chatham'],
  'Nicaragua': ['America/Managua'],
  'Nigeria': ['Africa/Lagos'],
  'Norway': ['Europe/Oslo'],
  'Oman': ['Asia/Muscat'],
  'Pakistan': ['Asia/Karachi'],
  'Panama': ['America/Panama'],
  'Paraguay': ['America/Asuncion'],
  'Peru': ['America/Lima'],
  'Philippines': ['Asia/Manila'],
  'Poland': ['Europe/Warsaw'],
  'Portugal': ['Europe/Lisbon', 'Atlantic/Azores', 'Atlantic/Madeira'],
  'Puerto Rico': ['America/Puerto_Rico'],
  'Qatar': ['Asia/Qatar'],
  'Romania': ['Europe/Bucharest'],
  'Russia': ['Europe/Moscow', 'Europe/Kaliningrad', 'Europe/Samara', 'Asia/Yekaterinburg', 'Asia/Omsk', 'Asia/Krasnoyarsk', 'Asia/Irkutsk', 'Asia/Yakutsk', 'Asia/Vladivostok', 'Asia/Magadan', 'Asia/Kamchatka'],
  'Saudi Arabia': ['Asia/Riyadh'],
  'Senegal': ['Africa/Dakar'],
  'Serbia': ['Europe/Belgrade'],
  'Singapore': ['Asia/Singapore'],
  'Slovakia': ['Europe/Bratislava'],
  'Slovenia': ['Europe/Ljubljana'],
  'South Africa': ['Africa/Johannesburg'],
  'South Korea': ['Asia/Seoul'],
  'Spain': ['Europe/Madrid', 'Atlantic/Canary'],
  'Sri Lanka': ['Asia/Colombo'],
  'Sudan': ['Africa/Khartoum'],
  'Sweden': ['Europe/Stockholm'],
  'Switzerland': ['Europe/Zurich'],
  'Syria': ['Asia/Damascus'],
  'Taiwan': ['Asia/Taipei'],
  'Tajikistan': ['Asia/Dushanbe'],
  'Tanzania': ['Africa/Dar_es_Salaam'],
  'Thailand': ['Asia/Bangkok'],
  'Tunisia': ['Africa/Tunis'],
  'Turkey': ['Europe/Istanbul'],
  'Turkmenistan': ['Asia/Ashgabat'],
  'Uganda': ['Africa/Kampala'],
  'Ukraine': ['Europe/Kiev'],
  'United Arab Emirates': ['Asia/Dubai'],
  'United Kingdom': ['Europe/London'],
  'United States': ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'America/Phoenix', 'America/Indiana/Indianapolis'],
  'Uruguay': ['America/Montevideo'],
  'Uzbekistan': ['Asia/Tashkent'],
  'Venezuela': ['America/Caracas'],
  'Vietnam': ['Asia/Ho_Chi_Minh'],
  'Yemen': ['Asia/Aden'],
  'Zambia': ['Africa/Lusaka'],
  'Zimbabwe': ['Africa/Harare'],
};

const COUNTRIES = Object.keys(COUNTRY_TIMEZONES).sort();

const handleCountryChange = (country, setFormData) => {
  const timezones = COUNTRY_TIMEZONES[country] || [];
  setFormData(prev => ({
    ...prev,
    country,
    timezone: timezones.length === 1 ? timezones[0] : '',
  }));
};

const Branches = () => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('table');
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    branchType: 'OFFICE',
    status: 'ACTIVE',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    managerName: '',
    phone: '',
    email: '',
    timezone: '',
    description: ''
  });
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [cmmsSubscription, setCmmsSubscription] = useState(null);
  const [showOverageModal, setShowOverageModal] = useState(false);

  // Export handler
  const handleExport = (format) => {
    const data = branches.map(b => ({
      code: b.code || '',
      name: b.name || '',
      branchType: b.branchType || '',
      status: b.status || '',
      address: b.address || '',
      city: b.city || '',
      state: b.state || '',
      country: b.country || '',
      postalCode: b.postalCode || '',
      managerName: b.managerName || '',
      phone: b.phone || '',
      email: b.email || '',
      timezone: b.timezone || '',
      locationCount: b.locationCount || 0,
      assetCount: b.assetCount || 0,
    }));

    const columns = [
      { key: 'code', label: 'Branch Code' },
      { key: 'name', label: 'Name' },
      { key: 'branchType', label: 'Type' },
      { key: 'status', label: 'Status' },
      { key: 'address', label: 'Address' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'country', label: 'Country' },
      { key: 'postalCode', label: 'Postal Code' },
      { key: 'managerName', label: 'Manager' },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'timezone', label: 'Timezone' },
      { key: 'locationCount', label: 'Locations' },
      { key: 'assetCount', label: 'Assets' },
    ];

    const filename = `branches-${getDateString()}`;
    if (format === 'csv') {
      exportToCSV(data, columns, filename);
    } else {
      exportToExcel(data, columns, filename);
    }
    setShowExportMenu(false);
  };

  useEffect(() => {
    loadBranches();
  }, []);

  useEffect(() => {
    subscriptionService.getCmmsSubscription()
      .then(data => setCmmsSubscription(data))
      .catch(() => {});
  }, []);

  const loadBranches = async () => {
    setLoading(true);
    try {
      const data = await branchService.getBranches();
      setBranches(data || []);
    } catch (error) {
      console.error('Error loading branches:', error);
      setBranches([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      ACTIVE: 'bg-green-100 text-green-800',
      INACTIVE: 'bg-gray-100 text-gray-800',
      UNDER_CONSTRUCTION: 'bg-yellow-100 text-yellow-800',
      CLOSED: 'bg-red-100 text-red-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getTypeIcon = (type) => {
    const icons = {
      HEADQUARTERS: Building,
      WAREHOUSE: Warehouse,
      FACTORY: Factory,
      OFFICE: Home,
      SERVICE_CENTER: Wrench,
      RETAIL: Store,
      DATA_CENTER: Server,
      LABORATORY: FlaskConical,
      OTHER: Building
    };
    return icons[type] || Building;
  };

  const getTypeLabel = (type) => {
    const labels = {
      HEADQUARTERS: t('cmms.branches_typeHeadquarters'),
      WAREHOUSE: t('cmms.branches_typeWarehouse'),
      FACTORY: t('cmms.branches_typeFactory'),
      OFFICE: t('cmms.branches_typeOffice'),
      SERVICE_CENTER: t('cmms.branches_typeServiceCenter'),
      RETAIL: t('cmms.branches_typeRetail'),
      DATA_CENTER: t('cmms.branches_typeDataCenter'),
      LABORATORY: t('cmms.branches_typeLaboratory'),
      OTHER: t('cmms.branches_typeOther')
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      HEADQUARTERS: 'bg-purple-100 text-purple-600',
      WAREHOUSE: 'bg-orange-100 text-orange-600',
      FACTORY: 'bg-blue-100 text-blue-600',
      OFFICE: 'bg-green-100 text-green-600',
      SERVICE_CENTER: 'bg-yellow-100 text-yellow-600',
      RETAIL: 'bg-pink-100 text-pink-600',
      DATA_CENTER: 'bg-indigo-100 text-indigo-600',
      LABORATORY: 'bg-cyan-100 text-cyan-600',
      OTHER: 'bg-gray-100 text-gray-600'
    };
    return colors[type] || 'bg-gray-100 text-gray-600';
  };

  const filteredBranches = branches.filter(branch => {
    const matchesStatus = filterStatus === 'all' || branch.status === filterStatus;
    const matchesType = filterType === 'all' || branch.branchType === filterType;
    const matchesSearch =
      branch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.city?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesType && matchesSearch;
  });

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedBranches(filteredBranches.map(b => b.id));
    } else {
      setSelectedBranches([]);
    }
  };

  const handleSelect = (id) => {
    setSelectedBranches(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleCreate = () => {
    setEditingBranch(null);
    setFormData({
      code: '',
      name: '',
      branchType: 'OFFICE',
      status: 'ACTIVE',
      address: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      managerName: '',
      phone: '',
      email: '',
      timezone: '',
      description: ''
    });
    const current = branches.length;
    const max = cmmsSubscription?.includedBranches ?? null;
    if (max !== null && current >= max) {
      setShowOverageModal(true);
    } else {
      setShowModal(true);
    }
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setFormData({
      code: branch.code,
      name: branch.name,
      branchType: branch.branchType,
      status: branch.status,
      address: branch.address || '',
      city: branch.city || '',
      state: branch.state || '',
      country: branch.country || '',
      postalCode: branch.postalCode || '',
      managerName: branch.managerName || '',
      phone: branch.phone || '',
      email: branch.email || '',
      timezone: branch.timezone || '',
      description: branch.description || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBranch) {
        await branchService.updateBranch(editingBranch.id, formData);
      } else {
        await branchService.createBranch(formData);
        subscriptionService.getCmmsSubscription()
          .then(data => setCmmsSubscription(data))
          .catch(() => {});
      }
      setShowModal(false);
      loadBranches();
    } catch (error) {
      console.error('Error saving branch:', error);
      alert('Failed to save branch. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('cmms.branches_deleteConfirm'))) {
      try {
        await branchService.deleteBranch(id);
        loadBranches();
      } catch (error) {
        console.error('Error deleting branch:', error);
      }
    }
  };

  // Stats
  const stats = {
    total: branches.length,
    active: branches.filter(b => b.status === 'ACTIVE').length,
    totalLocations: branches.reduce((sum, b) => sum + (b.locationCount || 0), 0),
    totalAssets: branches.reduce((sum, b) => sum + (b.assetCount || 0), 0)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('cmms.branches')}</h1>
          <p className="text-gray-600 mt-1">{t('cmms.branches_subtitle')}</p>
        </div>
        <div className="flex gap-3">
          {/* Export Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            >
              <Download className="w-5 h-5 mr-2" />
              {t('common.export')}
              <ChevronDown className="w-4 h-4 ml-2" />
            </button>
            {showExportMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                  <button
                    onClick={() => handleExport('excel')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-green-600" />
                    {t('common.exportExcel')}
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4 text-blue-600" />
                    {t('common.exportCsv')}
                  </button>
                </div>
              </>
            )}
          </div>
          <button
            onClick={handleCreate}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('common.add')}
          </button>
        </div>
      </div>

      {cmmsSubscription && (
        <UsageMeter
          label={t('cmms.branches')}
          current={branches.length}
          max={cmmsSubscription.includedBranches ?? null}
          className="mb-4"
        />
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-600">{t('cmms.branches_total')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.active}</div>
              <div className="text-sm text-gray-600">{t('common.active')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <MapPinned className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.totalLocations}</div>
              <div className="text-sm text-gray-600">{t('cmms.branches_totalLocations')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Server className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.totalAssets}</div>
              <div className="text-sm text-gray-600">{t('cmms.totalAssets')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('common.allStatus')}</option>
            <option value="ACTIVE">{t('common.active')}</option>
            <option value="INACTIVE">{t('common.inactive')}</option>
            <option value="UNDER_CONSTRUCTION">{t('cmms.branches_underConstruction')}</option>
            <option value="CLOSED">{t('cmms.branches_closed')}</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('common.allTypes')}</option>
            <option value="HEADQUARTERS">{t('cmms.branches_typeHeadquarters')}</option>
            <option value="WAREHOUSE">{t('cmms.branches_typeWarehouse')}</option>
            <option value="FACTORY">{t('cmms.branches_typeFactory')}</option>
            <option value="OFFICE">{t('cmms.branches_typeOffice')}</option>
            <option value="SERVICE_CENTER">{t('cmms.branches_typeServiceCenter')}</option>
            <option value="RETAIL">{t('cmms.branches_typeRetail')}</option>
            <option value="DATA_CENTER">{t('cmms.branches_typeDataCenter')}</option>
            <option value="LABORATORY">{t('cmms.branches_typeLaboratory')}</option>
          </select>
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 ${viewMode === 'table' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-600'}`}
            >
              {t('common.table')}
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-600'}`}
            >
              {t('common.grid')}
            </button>
          </div>
        </div>
      </div>

      {/* Table View */}
      {viewMode === 'table' ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={selectedBranches.length === filteredBranches.length && filteredBranches.length > 0}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.branches')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.type')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.address')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.branches_manager')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.locations')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.assets')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredBranches.map((branch) => {
                const TypeIcon = getTypeIcon(branch.branchType);
                return (
                  <tr key={branch.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedBranches.includes(branch.id)}
                        onChange={() => handleSelect(branch.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${getTypeColor(branch.branchType)}`}>
                          <TypeIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{branch.name}</div>
                          <div className="text-sm text-gray-500">{branch.code}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {getTypeLabel(branch.branchType)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="text-gray-900">{branch.city}, {branch.state}</div>
                        <div className="text-gray-500">{branch.country}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="text-gray-900">{branch.managerName}</div>
                        <div className="text-gray-500 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {branch.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {branch.locationCount || 0}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {branch.assetCount || 0}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(branch.status)}`}>
                        {branch.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/portal/cmms/branches/${branch.id}`}
                          className="p-1 text-gray-400 hover:text-blue-600"
                        >
                          <Eye className="w-5 h-5" />
                        </Link>
                        <button
                          onClick={() => handleEdit(branch)}
                          className="p-1 text-gray-400 hover:text-blue-600"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(branch.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        /* Grid View */
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBranches.map((branch) => {
            const TypeIcon = getTypeIcon(branch.branchType);
            return (
              <div key={branch.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getTypeColor(branch.branchType)}`}>
                      <TypeIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{branch.name}</h3>
                      <p className="text-sm text-gray-500">{branch.code}</p>
                    </div>
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(branch.status)}`}>
                    {branch.status.replace('_', ' ')}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="w-4 h-4" />
                    {branch.city}, {branch.state}, {branch.country}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <User className="w-4 h-4" />
                    {branch.managerName}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="w-4 h-4" />
                    {branch.phone}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Globe className="w-4 h-4" />
                    {branch.timezone}
                  </div>
                </div>

                <div className="flex items-center gap-4 py-3 border-t border-b border-gray-100 mb-4">
                  <div className="text-center flex-1">
                    <div className="text-lg font-semibold text-gray-900">{branch.locationCount || 0}</div>
                    <div className="text-xs text-gray-500">{t('cmms.locations')}</div>
                  </div>
                  <div className="text-center flex-1">
                    <div className="text-lg font-semibold text-gray-900">{branch.assetCount || 0}</div>
                    <div className="text-xs text-gray-500">{t('cmms.assets')}</div>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Link
                    to={`/portal/cmms/branches/${branch.id}`}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Eye className="w-5 h-5" />
                  </Link>
                  <button
                    onClick={() => handleEdit(branch)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(branch.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingBranch ? t('cmms.branches_edit') : t('cmms.branches_addNew')}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.branches_code')} *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder={t('cmms.branches_phCode')}
                    disabled={editingBranch}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder={t('cmms.branches_phName')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.type')} *</label>
                  <select
                    value={formData.branchType}
                    onChange={(e) => setFormData({...formData, branchType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="HEADQUARTERS">{t('cmms.branches_typeHeadquarters')}</option>
                    <option value="WAREHOUSE">{t('cmms.branches_typeWarehouse')}</option>
                    <option value="FACTORY">{t('cmms.branches_typeFactory')}</option>
                    <option value="OFFICE">{t('cmms.branches_typeOffice')}</option>
                    <option value="SERVICE_CENTER">{t('cmms.branches_typeServiceCenter')}</option>
                    <option value="RETAIL">{t('cmms.branches_typeRetail')}</option>
                    <option value="DATA_CENTER">{t('cmms.branches_typeDataCenter')}</option>
                    <option value="LABORATORY">{t('cmms.branches_typeLaboratory')}</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.status')}</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ACTIVE">{t('common.active')}</option>
                    <option value="INACTIVE">{t('common.inactive')}</option>
                    <option value="UNDER_CONSTRUCTION">{t('cmms.branches_underConstruction')}</option>
                    <option value="CLOSED">{t('cmms.branches_closed')}</option>
                  </select>
                </div>
              </div>

              {/* Address Section */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">{t('cmms.branches_addressInfo')}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.address')}</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder={t('cmms.branches_phAddress')}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.city')}</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.branches_stateProvince')}</label>
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => setFormData({...formData, state: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.branches_country')}</label>
                      <select
                        value={formData.country}
                        onChange={(e) => handleCountryChange(e.target.value, setFormData)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                      >
                        <option value="">— Select country —</option>
                        {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.branches_postalCode')}</label>
                      <input
                        type="text"
                        value={formData.postalCode}
                        onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.branches_timezone')}</label>
                    <select
                      value={formData.timezone}
                      onChange={(e) => setFormData({...formData, timezone: e.target.value})}
                      disabled={!formData.country}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <option value="">{formData.country ? '— Select timezone —' : t('cmms.branches_selectCountryFirst')}</option>
                      {(COUNTRY_TIMEZONES[formData.country] || []).map(tz => <option key={tz} value={tz}>{tz}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Contact Section */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">{t('cmms.branches_contactInfo')}</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.branches_managerName')}</label>
                    <input
                      type="text"
                      value={formData.managerName}
                      onChange={(e) => setFormData({...formData, managerName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.phone')}</label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.email')}</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={t('cmms.branches_phNotes')}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingBranch ? t('cmms.branches_update') : t('cmms.branches_create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <OverageConsentModal
        isOpen={showOverageModal}
        onClose={() => setShowOverageModal(false)}
        resourceLabel="branch"
        current={branches.length}
        max={cmmsSubscription?.includedBranches ?? 0}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.branches_code')} *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
                placeholder="e.g., HQ-001"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')} *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
                placeholder="e.g., Main Headquarters"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.type')} *</label>
              <select
                value={formData.branchType}
                onChange={(e) => setFormData({...formData, branchType: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="HEADQUARTERS">{t('cmms.branches_typeHeadquarters')}</option>
                <option value="WAREHOUSE">{t('cmms.branches_typeWarehouse')}</option>
                <option value="FACTORY">{t('cmms.branches_typeFactory')}</option>
                <option value="OFFICE">{t('cmms.branches_typeOffice')}</option>
                <option value="SERVICE_CENTER">{t('cmms.branches_typeServiceCenter')}</option>
                <option value="RETAIL">{t('cmms.branches_typeRetail')}</option>
                <option value="DATA_CENTER">{t('cmms.branches_typeDataCenter')}</option>
                <option value="LABORATORY">{t('cmms.branches_typeLaboratory')}</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.status')}</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="ACTIVE">{t('common.active')}</option>
                <option value="INACTIVE">{t('common.inactive')}</option>
                <option value="UNDER_CONSTRUCTION">{t('cmms.branches_underConstruction')}</option>
                <option value="CLOSED">{t('cmms.branches_closed')}</option>
              </select>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('cmms.branches_addressInfo')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.address')}</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Street address"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.city')}</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.branches_stateProvince')}</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.branches_country')}</label>
                  <select
                    value={formData.country}
                    onChange={(e) => handleCountryChange(e.target.value, setFormData)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">— Select country —</option>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.branches_postalCode')}</label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.branches_timezone')}</label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({...formData, timezone: e.target.value})}
                  disabled={!formData.country}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <option value="">{formData.country ? '— Select timezone —' : t('cmms.branches_selectCountryFirst')}</option>
                  {(COUNTRY_TIMEZONES[formData.country] || []).map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('cmms.branches_contactInfo')}</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.branches_managerName')}</label>
                <input
                  type="text"
                  value={formData.managerName}
                  onChange={(e) => setFormData({...formData, managerName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.phone')}</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.email')}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.description')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Additional notes about this branch..."
            />
          </div>
        </div>
      </OverageConsentModal>
    </div>
  );
};

export default Branches;
